/**
 * Shared utilities for standards preview functionality.
 * Used by both StandardsPreview and BipartiteChart components
 * to ensure consistent cache keys and API requests.
 */

import { TagCandidate, MathExpression, LIMITER_TYPE_MATHTASTIC } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface StandardsPreviewRequest {
  competency_id: string;
  limiter_ids: string[];
  selected_math_expression_ids?: string[];
  regex_target_composite?: {
    id: string;
    type: string;
    regexTargets: any[];
    order: number;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a limiter group is a mathtastic (P-type) group
 */
export function isMathtasticGroup(group: TagCandidate['limiter_groups'][0]): boolean {
  return group.limiters?.some(limiter => limiter.type === LIMITER_TYPE_MATHTASTIC) || false;
}

/**
 * Extract limiter IDs from non-mathtastic groups
 */
export function extractLimiterIds(tag: TagCandidate): string[] {
  const limiterIds: string[] = [];
  tag.limiter_groups?.forEach((group) => {
    if (group.selected && !isMathtasticGroup(group)) {
      group.limiters?.forEach((limiter) => {
        if (limiter.selected) {
          limiterIds.push(limiter.limiter_id);
        }
      });
    }
  });
  return limiterIds;
}

/**
 * Extract selected math expression IDs from mathtastic groups
 */
export function extractSelectedMathExpressionIds(tag: TagCandidate): string[] {
  const mathExprIds: string[] = [];
  tag.limiter_groups?.forEach((group) => {
    if (group.selected && isMathtasticGroup(group) && group.selected_math_expression_ids) {
      mathExprIds.push(...group.selected_math_expression_ids);
    }
  });
  return mathExprIds;
}

/**
 * Build regex_target_composite from selected math expression IDs
 */
function buildRegexTargetComposite(
  selectedMathExprIds: string[],
  mathExpressions: MathExpression[] | undefined
): StandardsPreviewRequest['regex_target_composite'] | undefined {
  if (!mathExpressions || selectedMathExprIds.length === 0) {
    return undefined;
  }

  const regexTargets: any[] = [];
  let orderCounter = 0;

  // Check both decomposed expressions and content expressions
  for (const mathExpr of mathExpressions) {
    // Check decomposed expressions
    for (const decomposed of mathExpr.decomposed || []) {
      if (selectedMathExprIds.includes(decomposed.id) && decomposed.spine) {
        regexTargets.push({
          ...decomposed.spine,
          order: orderCounter++,
        });
      }
    }
    // Check content expression
    if (
      mathExpr.contentExpression &&
      selectedMathExprIds.includes(mathExpr.contentExpression.id) &&
      mathExpr.contentExpression.spine
    ) {
      regexTargets.push({
        ...mathExpr.contentExpression.spine,
        order: orderCounter++,
      });
    }
  }

  if (regexTargets.length === 0) {
    return undefined;
  }

  return {
    id: `composite-${Date.now()}`,
    type: 'COMPOSITE',
    regexTargets,
    order: 0,
  };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Create a stable cache key for React Query.
 * Must be identical across components for cache sharing to work.
 *
 * @param tagCandidates - Array of tag candidates
 * @param selectedOnly - If true, only include selected tags
 * @returns Stable string key
 */
export function createStandardsKey(
  tagCandidates: TagCandidate[],
  selectedOnly: boolean
): string {
  const candidates = Array.isArray(tagCandidates) ? tagCandidates : [];

  return candidates
    .filter((tag) => selectedOnly ? tag?.competency?.selected : tag?.competency)
    .map((tag) => {
      const selectedLimiters: string[] = [];
      const selectedMathExprIds: string[] = [];

      tag.limiter_groups?.forEach((group) => {
        if (group.selected) {
          // Collect limiter IDs from non-mathtastic groups
          if (!isMathtasticGroup(group)) {
            group.limiters?.forEach((limiter) => {
              if (limiter.selected) {
                selectedLimiters.push(limiter.limiter_id);
              }
            });
          }
          // Collect math expression IDs from mathtastic groups
          if (isMathtasticGroup(group) && group.selected_math_expression_ids?.length) {
            selectedMathExprIds.push(...group.selected_math_expression_ids);
          }
        }
      });

      return `${tag.competency.spine_node_id}:${selectedLimiters.sort().join(',')}:${selectedMathExprIds.sort().join(',')}`;
    })
    .join('|');
}

/**
 * Build preview requests for the standards API.
 *
 * @param tagCandidates - Array of tag candidates
 * @param mathExpressions - Math expressions for building regex composites
 * @param selectedOnly - If true, only include selected tags
 * @returns Array of request objects for batchPreview API
 */
export function buildPreviewRequests(
  tagCandidates: TagCandidate[],
  mathExpressions: MathExpression[] | undefined,
  selectedOnly: boolean
): StandardsPreviewRequest[] {
  const candidates = Array.isArray(tagCandidates) ? tagCandidates : [];

  return candidates
    .filter((tag) => selectedOnly ? tag?.competency?.selected : tag?.competency)
    .map((tag) => {
      const limiterIds = extractLimiterIds(tag);
      const selectedMathExprIds = extractSelectedMathExpressionIds(tag);
      const regexTargetComposite = buildRegexTargetComposite(selectedMathExprIds, mathExpressions);

      return {
        competency_id: tag.competency.spine_node_id,
        limiter_ids: limiterIds,
        ...(selectedMathExprIds.length > 0 && { selected_math_expression_ids: selectedMathExprIds }),
        ...(regexTargetComposite && { regex_target_composite: regexTargetComposite }),
      };
    });
}
